import { QuestionIcon } from '@/_assets/icon-components';
import NumberToPrice from '@/_components/NumberToPrice';
import { ISingleOrder } from '@/_service/types/orders';
import { IOrderStepsProduct } from '@/_types';
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Rating, RoundedStar } from '@smastrom/react-rating';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import CRating from './components/CRating';

interface IProps {
  product: IOrderStepsProduct;
  orderData: ISingleOrder;
  refetch: () => void;
}

function ProductCard({ orderData, product, refetch }: IProps) {
  const [showMore, setShowMore] = useState(true);

  const t = useTranslations();

  const date = dayjs(product?.review?.created_at || null);
  dayjs.locale('ru');

  const formattedDate = date.format('D MMMM YYYY [г.]');

  return (
    <Box p='4'>
      <HStack justify='space-between' align='center'>
        <HStack w={'full'} justifyContent={'space-between'}>
          <HStack gap={'10px'} flex={1}>
            <Box
              bg={'light.300'}
              rounded={8}
              minW={{ base: '38px', lg: '64px' }}
              h={{ base: '38px', lg: '64px' }}
            >
              <AspectRatio
                minW='38px'
                ratio={1 / 1}
                overflow='hidden'
                borderRadius='md'
                position='relative'
                bg={'background'}
              >
                {product?.image ? (
                  <Image
                    src={process.env.BASE_URL + product?.image}
                    alt={'Photo - ' + product?.name}
                    style={{ objectFit: 'cover' }}
                    fill
                  />
                ) : (
                  <Box>
                    <QuestionIcon />
                  </Box>
                )}
              </AspectRatio>
            </Box>
            <VStack alignItems={'start'} gap={1}>
              <Text maxW={'200px'} fontWeight={500}>
                {product?.name}
              </Text>
              <Text fontSize='sm' lineHeight={1}>
                {product?.quantity} шт
              </Text>
            </VStack>
          </HStack>

          <Stack gap='0' alignItems='end'>
            <Box alignItems={'center'}>
              <NumberToPrice
                value={
                  (product?.total_amount || 0) +
                  (product?.total_modifier_amount || 0)
                }
                textProps={{
                  fontWeight: 600,
                }}
                spanProps={{
                  fontWeight: 600,
                }}
              />
            </Box>

            <Flex fontSize={14} color={'gray.600'}>
              <NumberToPrice
                value={
                  (product?.price || 0) + (product?.total_modifier_amount || 0)
                }
                textProps={{
                  fontWeight: 400,
                  fontSize: 14,
                }}
                spanProps={{
                  fontWeight: 400,
                  fontSize: 14,
                }}
              />
              /{t('pcs')}
            </Flex>
          </Stack>
        </HStack>
      </HStack>

      <HStack mt={3} alignItems='end' justifyContent={'end'} gap={3}>
        <CRating
          review={product?.review}
          productId={product?.product_id}
          orderData={orderData}
          product={product}
          refetch={refetch}
        />
      </HStack>

      {product?.review?.id && (
        <Stack p={4} mt={2} rounded={16} bg={'#F5F4F3'}>
          <Flex justifyContent={'space-between'}>
            <Text fontWeight={500}>{t('your_feedback')}</Text>
            <Text>{formattedDate}</Text>
          </Flex>
          <Box>
            {/* <Text>{product?.review?.rating}</Text> */}
            <Rating
              readOnly={Boolean(product?.review?.rating)}
              style={{ maxWidth: 100 }}
              value={product?.review?.rating || 0}
              itemStyles={{
                itemShapes: RoundedStar,
                activeFillColor: '#FFB338',
                inactiveFillColor: '#E0E8F1',
              }}
            />
            <Text mt={2} fontSize={14} fontWeight={400} lineHeight={'17px'}>
              {showMore
                ? product?.review?.review_message
                : product?.review?.review_message?.substring(0, 140) + '...'}
            </Text>
            {product?.review?.review_message?.length > 140 && (
              <Button
                w='fit-content'
                color={'primary'}
                fontSize='xs'
                fontWeight={400}
                transform={`translateY(${showMore ? 0 : -5}px)`}
                onClick={() => setShowMore((prev) => !prev)}
              >
                {showMore ? t('show_less') : t('show_more')}
              </Button>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}

export default ProductCard;
